/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { withTranslation } from 'react-i18next';
import { Input, message } from 'antd';
import { useHistory } from 'react-router-dom';
import md5 from 'md5';
import { PASSWORD_MAX_LENGTH, EMAIL_MAX_LENGTH, ENTERPRISE_ENV } from '../../utils/helper';
import Loader from '../../components/Loader';
import TitleBanner from '../../components/TitleBanner';
import { preventAction } from './Tools';
import { RESET_USER_PASSWORD, USER_LOGIN } from './translationValues';
import { AppStoreContext } from '../../context/appStoreContext';
import { ssoAuthenticate } from '../../utils/services/authAPI';

import { LoginContainer, SubmitButton } from './styles';
import {
  ModalBody,
  ModalContainer,
  InputLabel,
  InputContainer,
  RightAlign,
} from '../../components/Modals/styles';
import Regex from '../../utils/regex';
import { signIn, sendPasswordResetEmail } from 'supertokens-web-js/recipe/emailpassword';
import { Icon } from 'antd';
import i18n from '../../i18n';
import { notifyVendorPostSsoLogin } from '../../utils/embedHelper';
import { verifySso } from '../../utils/services/ssoAPI';

const NAMESPACES = ['USER_LOGIN', 'RESET_USER_PASSWORD'];
const RESET_USER_PWD = 'RESET_USER_PASSWORD';

const Login = props => {
  const { setCurrentPage, t, initialLoading = false, onLoginComplete } = props;
  const history = useHistory();
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loginView, setLoginView] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSSOLoading] = useState(initialLoading);
  const [tenantName, setTenantName] = useState('');

  const { ssoAuthClient: authClient, ssoSetup } = useContext(AppStoreContext);
  const [imageLoaded, setImageLoad] = useState(ssoSetup?.ssoEnabled);

  const resetButtonState = () => {
    setDisabled(false);
    setLoading(false);
    setSSOLoading(false);
  }

  // login
  async function signInClicked(email, password) {

    try {
      let response = await signIn({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: md5(password),
          },
        ]
      });

      if (response.status !== 'OK') {
        resetButtonState();
      }

      if (response.status === 'FIELD_ERROR') {
        response.formFields.forEach(formField => {
          if (formField.id === 'email') {
            console.log('formField', formField)
            // Email validation failed (for example incorrect email syntax).
            message.error(formField.error);
          }
        });
      } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        message.error(t(USER_LOGIN.WRONG_CREDENTIAL_COMBINATION_ERROR_MESSAGE.key, USER_LOGIN.WRONG_CREDENTIAL_COMBINATION_ERROR_MESSAGE.value));
      } else if (response.status === 'GENERAL_ERROR') {
        message.error(t(USER_LOGIN.SOMETHING_WENT_WRONG_ERROR_MESSAGE.key, USER_LOGIN.SOMETHING_WENT_WRONG_ERROR_MESSAGE.value));

      } else {
        // sign in successful. The session tokens are automatically handled by
        // the frontend SDK.
        if (onLoginComplete) onLoginComplete();
        // window.location.href = "/"
        return response;
      }
    } catch (err) {
      resetButtonState();
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        console.log('error', err.message)
        message.error(err.message);
      } else {
        message.error(t(USER_LOGIN.GENERAL_ERROR_MESSAGE.key, USER_LOGIN.GENERAL_ERROR_MESSAGE.value));
      }
    }
  };

  // forgot password 
  const forgotHandler = async () => {
    // trigger resetpassword      
    try {
      const response = await sendPasswordResetEmail({
        formFields: [
          {
            id: 'email',
            value: email,
          },
        ]
      });

      if (response.status !== 'OK') {
        resetButtonState();
      };


      if (response.status === 'FIELD_ERROR') {
        response.formFields.forEach(formField => {
          if (formField.id === 'email') {
            message.error(formField.error);
          }
        });
      } else if (response.status === 'GENERAL_ERROR') {
        message.error(t(USER_LOGIN.SOMETHING_WENT_WRONG_ERROR_MESSAGE.key, USER_LOGIN.SOMETHING_WENT_WRONG_ERROR_MESSAGE.value));
      } else {
        setCurrentPage('emailVerification');
      }
    }
    catch (err) {
      resetButtonState();
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        message.error(err.message);
      } else {
        message.error(t(USER_LOGIN.GENERAL_ERROR_MESSAGE.key, USER_LOGIN.GENERAL_ERROR_MESSAGE.value));
      }
    }
  };

  // Code to check if all fields are empty
  // Return True if any  fields are empty, 
  const checkAllFields = () => {
    if ((loginView && password.trim().length !== 0 && email.length !== 0) || (!loginView && email.length !== 0)) {
      return (false);
    } else {
      return (true);
    }
  }

  const handleKeyPress = e => {
    if ((!checkAllFields() && e.key === 'Enter')) {
      handleLogin();
    }
  };

  // email validation
  const checkEmail = async email => {
    if (email.length === 0) return;
    const isValidEmail = Regex.EMAIL.test(email);

    if (isValidEmail) {
      setEmailError(false);
    } else {
      setEmailError(true);
      return (true);
    }
  };

  const handleLogin = async () => {
    if (loading || disabled) return;

    const response = await checkEmail(email);
    if (response) return;

    setLoading(true);
    setDisabled(true);

    if (loginView) {
      await signInClicked(email, password);
    } else {
      await forgotHandler();
    }
  };

  useEffect(() => {
    let didCancel = false;

    const loadUser = async () => {
      if (!authClient) {
        return;
      }
        try {
          const hasAuthCode = await authClient?.isReturningFromAuthServer();
          if (!hasAuthCode) {
            console.log('no auth code detected...');
            // TODO: Handle failure
          } else {
            console.log({ authClient, didCancel });
            const token = !didCancel ? await authClient?.getAccessToken() : null;
            console.log({ token });
            if (token) {
              const response = await ssoAuthenticate(token?.token?.value);
              if (response) {
                localStorage.setItem('sso_signIn', true);
                history.push('/');
                notifyVendorPostSsoLogin(true);
              } else {
                notifyVendorPostSsoLogin(false);
              }
            } else {
              notifyVendorPostSsoLogin(false);
            }
          }
        } catch (err) {
          console.error("Error in establishing sso auth client ::: ", err);
          // TODO: Handle failure
          notifyVendorPostSsoLogin(false);
        } 
    };
    if (ssoLoading) {
      loadUser();
    }
    console.log({ ssoLoading, authClient });
    return () => {
      didCancel = true;
    };
  }, [ssoLoading, authClient]);

  const loginWithSSO = useCallback(async () => {
    if (!authClient) return;
    const trimmedTenantName = ssoSetup?.isMultiTenant ? tenantName.trim() : ssoSetup.tenantName;
    if (ssoSetup?.isMultiTenant && !trimmedTenantName) {
      message.error('Please enter your tenant name');
      return;
    }
    setSSOLoading(true);
    const response = await verifySso(trimmedTenantName);
    if (response?.data?.ssoEnabled) {
      // store the from url before redirecting ... we need this to correctly initialize the oauthClient after getting redirected back from SSO Provider.
      // localStorage.setItem(APP_FROM_URL, from);
      // Initiate the login flow
      await authClient.fetchAuthorizationCode({
        tenant: trimmedTenantName,
        product: ssoSetup.product,
      });
    } else {
      setSSOLoading(false);
      message.error('Please enter a valid tenant name');
    }
  }, [authClient, ssoSetup, tenantName]);

  useEffect(() => {
    if (!checkAllFields()) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [password, emailError, email]);

  const resetState = () => {
    setEmail('');
    setEmailError(false);
    setPassword('');
    setDisabled(true);
  };

  const onImageError = useCallback(() => {
    console.log("Image error received!!!");
    setImageLoad(false);
  }, []);

  //  reset all state when we leave the page
  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  useEffect(() => {
    //  The field can be prefilled on the very first page loading by the browser  
    const checkAutofilled = () => {
      const emailAutofilled = !!document.getElementById('emailInput')?.matches('*:-webkit-autofill');
      const passwordAutofilled = !!document.getElementById('passwordInput')?.matches('*:-webkit-autofill');
      emailAutofilled && passwordAutofilled && setDisabled(false);
    }
    // The time when it's ready is not very stable, so check few times
    setTimeout(checkAutofilled, 500);
    setTimeout(checkAutofilled, 1000);
    setTimeout(checkAutofilled, 1500)
    //Setting the application language to browser language
    setTimeout(() => {
      i18n.changeLanguage(navigator.language || navigator.userLanguage);
    }, 500);
  }, [])

  const renderLogin = () => (
    <>
      <InputContainer password>
        <InputLabel>{t(USER_LOGIN.LOGIN_USERNAME_LABEL.key, USER_LOGIN.LOGIN_USERNAME_LABEL.value)}</InputLabel>
        <Input
          id='emailInput'
          name="Email"
          size="large"
          placeholder={t(USER_LOGIN.LOGIN_USERNAME_PLACEHOLDER.key, USER_LOGIN.LOGIN_USERNAME_PLACEHOLDER.value)}
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={e => checkEmail(e.target.value)}
          onPaste={preventAction}
          onCopy={preventAction}
          className="inputBox "
          maxLength={EMAIL_MAX_LENGTH}
        />
        {emailError && email && (
          <InputLabel signUp error className="bottom_top_anime">
            {t(USER_LOGIN.INVALID_USER_EMAIL_ERROR_MESSAGE.key, USER_LOGIN.INVALID_USER_EMAIL_ERROR_MESSAGE.value)}
          </InputLabel>
        )}
      </InputContainer>
      <InputContainer password>
        <InputLabel>{t(USER_LOGIN.LOGIN_PASSWORD_LABEL.key, USER_LOGIN.LOGIN_PASSWORD_LABEL.value)}</InputLabel>
        <Input.Password
          size="large"
          id='passwordInput'
          placeholder={t(USER_LOGIN.LOGIN_PASSWORD_PLACEHOLDER.key, USER_LOGIN.LOGIN_PASSWORD_PLACEHOLDER.value)}
          value={password}
          onChange={e => setPassword(e.target.value)}
          onPaste={preventAction}
          onCopy={preventAction}
          maxLength={PASSWORD_MAX_LENGTH}
        />
      </InputContainer>
      <RightAlign>
        <a
          onClick={() => {
            setLoginView(false);
            setEmail(email);
            setCurrentPage('createPassword');
            resetState();
          }}
          className="forgot-btn"
        >
          {t(USER_LOGIN.FORGOT_PASSWORD_ACTION.key, USER_LOGIN.FORGOT_PASSWORD_ACTION.value)}
        </a>
      </RightAlign>
    </>
  );

  const renderForgotPassword = () => (
    <>
      <InputContainer>
        <InputLabel>{t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.RESET_PASSWORD_USERNAME_LABEL.key}`, RESET_USER_PASSWORD.RESET_PASSWORD_USERNAME_LABEL.value)}</InputLabel>
        <Input
          name="forgot-email"
          size="large"
          placeholder={t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.RESET_PASSWORD_USERNAME_PLACEHOLDER.key}`, RESET_USER_PASSWORD.RESET_PASSWORD_USERNAME_PLACEHOLDER.value)}
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={e => checkEmail(e.target.value)}
          onPaste={preventAction}
          onCopy={preventAction}
          maxLength={EMAIL_MAX_LENGTH}
        />
        {emailError && (
          <InputLabel signUp error className="bottom_top_anime">
            {t(USER_LOGIN.INVALID_USER_EMAIL_ERROR_MESSAGE.key, USER_LOGIN.INVALID_USER_EMAIL_ERROR_MESSAGE.value)}
          </InputLabel>
        )}

        <RightAlign>
          <a
            onClick={() => {
              setLoginView(true);
              resetState();
              setCurrentPage('login');
            }}
            className='login-btn'
          >
            {t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.BACK_TO_LOGIN_ACTION.key}`, RESET_USER_PASSWORD.BACK_TO_LOGIN_ACTION.value)}
          </a>
        </RightAlign>
      </InputContainer>
    </>
  );

  if (initialLoading) {
    return (
      <LoginContainer style={{ width: '100%' }}>
        <Loader />
      </LoginContainer>
    )
  }

  return (
      <LoginContainer onKeyDown={e => e.stopPropagation()} onKeyPress={handleKeyPress}>
        <TitleBanner
          title={loginView ? 'PERFECT DOC STUDIO' : t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.RESET_PASSWORD_TITLE.key}`, RESET_USER_PASSWORD.RESET_PASSWORD_TITLE.value)}
        />
        <p className="usage">
          {
            loginView
              ?
              t(USER_LOGIN.LOGIN_USER_DESCRIPTION.key, USER_LOGIN.LOGIN_USER_DESCRIPTION.value)
              :
              t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.RESET_PASSWORD_DESC.key}`, RESET_USER_PASSWORD.RESET_PASSWORD_DESC.value)
          }
        </p>
        <ModalBody className="content-body">
          <ModalContainer>{loginView ? renderLogin() : renderForgotPassword()}</ModalContainer>
        </ModalBody>
        <div className="footer">
          {
            loginView
              ? (
                <>
                <SubmitButton
                  key="submit"
                  type="primary"
                  disable={disabled}
                  onClick={handleLogin}
                >
                  <div className="wrap-1">
                    <div> {loading ? t(USER_LOGIN.LOGGING_IN_USER_BUTTON.key, USER_LOGIN.LOGGING_IN_USER_BUTTON.value) : t(USER_LOGIN.LOGIN_USER_BUTTON.key, USER_LOGIN.LOGIN_USER_BUTTON.value)}</div>
                    {loading ? <Icon type="loading" className="loading" /> : <div className="arrow"></div>}
                  </div>
                  <div className="wrap-2">
                    <div>
                      {t(USER_LOGIN.LOGIN_USER_BUTTON.key, USER_LOGIN.LOGIN_USER_BUTTON.value)}
                    </div>
                    <div className="arrow"></div>
                  </div>
                </SubmitButton>
                {ssoSetup?.ssoEnabled &&
                  <>
                    <p> or log in with </p>
                    {ssoSetup?.isMultiTenant && (
                      
                      <InputContainer password style={{minWidth: '350px', maxWidth: '350px', width: '100%'}}>
                        <InputLabel style={{textAlign: 'left'}}>Tenant Name</InputLabel>
                        <Input
                          size="large"
                          id='tenantNameInput'
                          placeholder="Enter your tenant name"
                          value={tenantName}
                          onChange={e => setTenantName(e.target.value)}
                          className="inputBox"
                        />
                    </InputContainer>
                    )}
                    {imageLoaded ? (
                      <SubmitButton className="image-button">
                        <div className='wrap-1'>
                          <img
                            src={'partner-logo.png'}
                            alt="partner-logo"
                            className={'no-image'}
                            onClick={loginWithSSO}
                            style={{
                              width: 150,
                              objectFit: 'contain',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                        <div className='wrap-2'>
                          <img
                            src={'partner-logo.png'}
                            alt="partner-logo"
                            className={'no-image'}
                            onError={onImageError}
                            onClick={loginWithSSO}
                            style={{
                              width: 150,
                              objectFit: 'contain',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                      </SubmitButton>
                    ) : (<SubmitButton
                      key="submit"
                      type="primary"
                      className="sso-login"
                      onClick={loginWithSSO}
                    >
                      <div className="wrap-1">
                        <div> {ssoLoading ? t(USER_LOGIN.LOGGING_IN_USER_BUTTON.key, USER_LOGIN.LOGGING_IN_USER_BUTTON.value) : 'Single Sign On'}</div>
                        {ssoLoading ? <Icon type="loading" className="loading" /> : <div className="arrow"></div>}
                      </div>
                      <div className="wrap-2">
                        <div>
                          {'Single Sign On'}
                        </div>
                        <div className="arrow"></div>
                      </div>
                    </SubmitButton>)}
                  </>}
                </>
              )
              : (
                <SubmitButton
                  key="submit"
                  type="primary"
                  disable={disabled}
                  onClick={() =>
                    emailSent ? [setLoginView(true), setEmailSent(false)] : handleLogin()
                  }
                >
                  <div className="wrap-1">
                    {
                      emailSent
                        ?
                        t(USER_LOGIN.BACK_TO_LOGIN_BUTTON.key, USER_LOGIN.BACK_TO_LOGIN_BUTTON.value)
                        :
                        t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.SEND_RESET_PASSWORD_EMAIL_BUTTON.key}`, RESET_USER_PASSWORD.SEND_RESET_PASSWORD_EMAIL_BUTTON.value)
                    }
                    {loading ? <Icon type="loading" className="loading" /> : <div className="arrow"></div>}
                  </div>
                  <div className="wrap-2">
                    {
                      emailSent
                        ?
                        t(USER_LOGIN.BACK_TO_LOGIN_BUTTON.key, USER_LOGIN.BACK_TO_LOGIN_BUTTON.value)
                        :
                        t(`${RESET_USER_PWD}:${RESET_USER_PASSWORD.SEND_RESET_PASSWORD_EMAIL_BUTTON.key}`, RESET_USER_PASSWORD.SEND_RESET_PASSWORD_EMAIL_BUTTON.value)
                    }
                    <div className="arrow"></div>
                  </div>
                </SubmitButton>
              )
          }
          {!ENTERPRISE_ENV && (
          <div className="redirect-btn">
            {t(USER_LOGIN.REDIRECT_TO_CREATE_ACCOUNT_DESCRIPTION.key, USER_LOGIN.REDIRECT_TO_CREATE_ACCOUNT_DESCRIPTION.value)}?{' '}
            <a
              onClick={() => {
                setCurrentPage('register');
              }}
            >              
             {' '}{t(USER_LOGIN.REDIRECT_TO_CREATE_ACCOUNT_ACTION.key, USER_LOGIN.REDIRECT_TO_CREATE_ACCOUNT_ACTION.value)}{' '}
            </a>
          </div>
          )}
        </div>
      </LoginContainer>
  );
};

export default withTranslation(NAMESPACES)(Login);
